import { ToastLevel } from "@metsooutotec/modes-react-components"
import * as helper from "../helpers/svg-helpers"
import * as editorTypes from "../editor-utils/editor-types"
import asEditorStore from "../local-store/asset-section-editor-store"
import customerStore, { CustomerStoreResults } from "features/organizations/customers/store/customer-store"
import { ModalTypes } from "features/organizations/customers/utils/customer-modal-helper"
import { LinerDrawingReference, PutAssetSectionLinersDto } from "features/organizations/asset-sections/models/asset-section-models"
import linerLocationsStore from "features/organizations/asset-sections/store/liner-locations-store"

export const onSaveSettingsAndDetails = async ({
    newEditAssetSection,
    fetchImage,
    setLoadingAssetImage,
    errorMessage,
    assetSectionImage,
    setMessage,
    setNewEditAssetSection,
}: editorTypes.onSaveSettingsAndDetailsprops) => {
    try {
        setLoadingAssetImage(true)
        if (!errorMessage) {
            if (newEditAssetSection?.assetSectionSvg == null || (newEditAssetSection?.assetSectionSvg?.size as number) <= 0) {
                newEditAssetSection.assetSectionSvg = await helper.ClearSVGBeforeSave(assetSectionImage as string)
            }
            const requestResult: CustomerStoreResults = await customerStore.editEntity(newEditAssetSection, ModalTypes.AssetSectionModal)
            if (requestResult.passed) {
                const { setAssetSectionSettings } = asEditorStore
                setAssetSectionSettings({
                    sensorNotificationArea: newEditAssetSection.sensorNotificationArea,
                    radiusHint: newEditAssetSection.radiusHint,
                    lineWeight: newEditAssetSection.lineWeight,
                })
                setNewEditAssetSection({ ...newEditAssetSection, assetSectionPdf: null, assetSectionSvg: null })
                setMessage(
                    'All changes have been made successfully. You can proceed to the next page by pressing the "Next" button.',
                    ToastLevel.SUCCESS,
                    6000,
                )
            } else {
                setMessage("Something went wrong, please read the logs", ToastLevel.ALARM, 6000)
                console.error(requestResult.message)
            }
        }
    } catch (e) {
        console.error(e)
    } finally {
        setLoadingAssetImage(false)
    }
}

export const onSaveLiners = async ({
    selectedLiners,
    setSelectedLiners,
    assetSectionId,
    errorMessage,
    assetSectionImage,
    setLoadingAssetImage,
    setMessage,
}: editorTypes.onSaveLinersProps) => {
    try {
        if (!selectedLiners) {
            setMessage("There is no selected liners to be saved!", ToastLevel.WARNING, 6000)
            return
        }
        setLoadingAssetImage(true)
        if (!errorMessage) {
            const assetSectionFile = await helper.ClearSVGBeforeSave(assetSectionImage as string)

            const requestModel: PutAssetSectionLinersDto = { id: assetSectionId as string, liners: selectedLiners }
            let requestResult = await linerLocationsStore.PutLinerLocations(requestModel)

            if (requestResult.passed) {
                requestResult = await linerLocationsStore.PutAssetSectionImage({ id: assetSectionId as string, newAssetSectionSvg: assetSectionFile })
                if (requestResult.passed) {
                    setMessage(
                        'All changes have been made successfully. You can proceed to the next page by pressing the "Next" button.',
                        ToastLevel.SUCCESS,
                        6000,
                    )
                    setSelectedLiners([])
                }
            } else {
                setMessage("Something went wrong, please read the logs", ToastLevel.ALARM, 6000)
            }
        } else {
            setMessage(errorMessage, ToastLevel.ALARM, 6000)
        }
    } catch (e) {
        setMessage("An unexpected error occurred. Please try again later.", ToastLevel.ALARM, 6000)
        console.error(e)
    } finally {
        setLoadingAssetImage(false)
    }
}

export const onSaveHoles = async ({
    selectedholes,
    assetSectionId,
    setSelectedHoles,
    setLoadingAssetImage,
    assetSectionImage,
    errorMessage,
    setMessage,
}: editorTypes.onSaveHolesProps) => {
    try {
        if (!selectedholes) {
            setMessage("There is no selected holes to be saved!", ToastLevel.WARNING, 6000)
            return
        }

        setLoadingAssetImage(true)
        if (!errorMessage) {
            const assetSectionFile = await helper.ClearSVGBeforeSave(assetSectionImage as string)

            let requestResult = await linerLocationsStore.PutAssetSectionHoles(assetSectionId, selectedholes)

            if (requestResult.passed) {
                requestResult = await linerLocationsStore.PutAssetSectionImage({ id: assetSectionId as string, newAssetSectionSvg: assetSectionFile })
                if (requestResult.passed) {
                    setMessage(
                        'All changes have been made successfully. You can proceed to the next page by pressing the "Next" button.',
                        ToastLevel.SUCCESS,
                        6000,
                    )
                    setSelectedHoles([])
                }
            } else {
                setMessage("Something went wrong, please read the logs", ToastLevel.ALARM, 6000)
            }
        } else {
            setMessage(errorMessage, ToastLevel.ALARM, 6000)
        }
    } catch (e) {
        setMessage("An unexpected error occurred. Please try again later.", ToastLevel.ALARM, 6000)
        console.error(e)
    } finally {
        setLoadingAssetImage(false)
    }
}

export const onSaveLinerDrawingReferences = async ({
    selectedLinersDrawingRef,
    assetSectionId,
    setSelectedLinersDrawingRef,
    setLoadingAssetImage,
    assetSectionImage,
    errorMessage,
    setMessage,
}: editorTypes.onSaveLinerDrawingReferencesProps) => {
    try {
        if (!selectedLinersDrawingRef) {
            setMessage("There is no selected liners to be saved!", ToastLevel.WARNING, 6000)
            return
        }
        setLoadingAssetImage(true)
        if (!errorMessage) {
            const requestModel: LinerDrawingReference[] = selectedLinersDrawingRef
            const requestResult = await linerLocationsStore.EditLinerLocationsDrawingReference(assetSectionId, requestModel)
            if (requestResult.passed) {
                setMessage(
                    'All changes have been made successfully. You can proceed to the next page by pressing the "Next" button.',
                    ToastLevel.SUCCESS,
                    6000,
                )
                setSelectedLinersDrawingRef([])
            } else {
                setMessage("Something went wrong, please read the logs", ToastLevel.ALARM, 6000)
            }
        } else {
            setMessage(errorMessage, ToastLevel.ALARM, 6000)
        }
    } catch (e) {
        setMessage("An unexpected error occurred. Please try again later.", ToastLevel.ALARM, 6000)
        console.error(e)
    } finally {
        setLoadingAssetImage(false)
    }
}

export const onSaveWearReferencePlane = async ({
    selectedWearReferencePlane,
    assetSectionId,
    setSelectedWearReferencePlane,
    assetSectionImage,
    errorMessage,
    setMessage,
    setLoadingAssetImage,
}: editorTypes.onSaveWearReferencePlaneProps) => {
    try {
        if (!selectedWearReferencePlane) {
            setMessage("There is no selected liners to be saved!", ToastLevel.WARNING, 6000)
            return
        }
        setLoadingAssetImage(true)
        if (!errorMessage) {
            const requestResult = await linerLocationsStore.EditLinerLocationsForWearReferencePlane(assetSectionId, selectedWearReferencePlane)
            if (requestResult.passed) {
                setMessage(
                    'All changes have been made successfully. You can proceed to the next page by pressing the "Next" button.',
                    ToastLevel.SUCCESS,
                    6000,
                )
                setSelectedWearReferencePlane([])
            } else {
                setMessage("Something went wrong, please read the logs", ToastLevel.ALARM, 6000)
            }
        } else {
            setMessage(errorMessage, ToastLevel.ALARM, 6000)
        }
    } catch (e) {
        setMessage("An unexpected error occurred. Please try again later.", ToastLevel.ALARM, 6000)
        console.error(e)
    } finally {
        setLoadingAssetImage(false)
    }
}
